/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './related-data-chart.component.html';

import {Downloader} from '../../../components/util/downloader';
import {StatsApiService} from '../controller/stats-api.service';
import {RELATED_CHART_DATA} from './related-data-chart-config';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_STAT_TYPES} from '../options/chart-options';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferSignalStateWrite} from '../util/template-state';
import {CHART_IMPORTS} from './chart-imports';
import {ChartOptionsComponent} from './chart-options/chart-options.component';
import {RelatedDataDomainComponent} from './related-data-domain.component';
import {RelatedDataLocationComponent} from './related-data-location.component';
import {RelatedDataTrafficComponent} from './related-data-traffic.component';

@Component({
  imports: [
    ...CHART_IMPORTS,
    ChartOptionsComponent,
    RelatedDataDomainComponent,
    RelatedDataLocationComponent,
    RelatedDataTrafficComponent
  ],
  selector: 'adn-related-data-chart',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelatedDataChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() obj: any;
  @Input() objName: string;
  @Input() key: string;
  @Input() chartType: string;
  @Input() dateSelectorId: string;
  @Input() relatedDataType: string;

  readonly pieError = signal<any>(null);
  relatedChartData: any;
  dateOption: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  statType = ADN_CHART_STAT_TYPES.impression;
  readonly allStats = signal<any>({});
  readonly totals = signal<any>(null);
  isSpecialUser = false;
  readonly osEmpty = signal(false);
  readonly mobileBrandEmpty = signal(false);
  readonly locationsEmpty = signal(false);
  readonly domainsEmpty = signal(false);
  readonly appEmpty = signal(false);
  readonly isPending = signal(false);

  readonly changeHook = {
    chartChangeHandlers: [] as Array<(totals: any) => void>,
    getTotals: () => this.totals()
  };

  get resolvedChartType(): string {
    return this.chartType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get resolvedRelatedDataType(): string {
    return this.relatedDataType;
  }

  private params: Record<string, any> = {};
  private statsSubscription?: Subscription;
  private lastRequestKey = '';
  private setupKey = '';
  private destroyed = false;

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly adnLog: AdnLogService,
    private readonly translate: TranslateService,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
  ) {
  }

  ngOnInit(): void {
    this.ensureStatsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.obj || changes.key || changes.chartType
      || changes.relatedDataType || changes.dateSelectorId) {
      this.setupKey = '';
      this.lastRequestKey = '';
    }

    this.ensureStatsLoaded();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.lastRequestKey = '';
    this.statsSubscription?.unsubscribe();
  }

  changeCallback(
    dateOptionId: string,
    startDate: any,
    endDate: any,
    countTypeId: string,
    statTypeId: string
  ): void {
    const chartOptions = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    const localUser = this.localUserProfile.get();
    const profile = localUser.profile;

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[dateOptionId];
    if (_.get(this.dateOption, 'id') === 'useDates' && this.obj?.startDate && this.obj?.endDate) {
      this.params.startDate = moment(this.obj.startDate);
      this.params.endDate = moment(this.obj.endDate);
    } else {
      this.params.startDate = startDate ? moment(startDate) : startDate;
      this.params.endDate = endDate ? moment(endDate) : endDate;
    }
    this.countType = ADN_CHART_COUNT_TYPES[countTypeId];
    this.statType = ADN_CHART_STAT_TYPES[statTypeId];
    this.lastRequestKey = '';
    this.ensureStatsLoaded();

    profile[chartOptions.perfChartOptions] = _.assign({}, profile[chartOptions.perfChartOptions], {
      dateOptionId: _.get(this.dateOption, 'id'),
      startDate: this.params.startDate ? this.params.startDate.toDate() : null,
      endDate: this.params.endDate ? this.params.endDate.toDate() : null,
      countTypeId: this.countType.id,
      statTypeId: this.statType.id
    });

    firstValueFrom(this.UserProfile.saveProfile({
      id: localUser.userId,
      profile: _.pick(profile, chartOptions.perfChartOptions)
    })).then((apiUser: any) => {
      this.localUserProfile.safeSaveProfile(chartOptions.perfChartOptions, apiUser.profile[chartOptions.perfChartOptions]);
    });
  }

  save(): void {
    const allStats = this.allStats();
    const refData = _.keyBy(_.map(allStats.chunks, (chunk) => chunk), 'chunkStart');

    let dataFields: Array<{name: string; id: string; groupId?: string}> = [{name: 'Date', id: 'chunkStart'}];
    _.forEach(this.relatedChartData.dataTypes, (dataType: any) => {
      dataFields = dataFields.concat(_.map(allStats.totals[dataType.id], (d, k) => {
        return {
          name: dataType.keyed ? this.translate.instant(dataType.keyed + '.' + k) || k : k,
          id: k,
          groupId: dataType.id
        };
      }));
    });
    const headingFields = _.map(dataFields, 'name');
    const fields = _.map(dataFields, 'id');
    const rows: any[] = [];

    _.forEach(allStats.chunkRange, (chunkStart) => {
      const rowData: Record<string, any> = {};
      _.forEach(dataFields, (field) => {
        if (field.id === 'chunkStart') {
          rowData[field.id] = chunkStart;
        } else {
          const refDatum = refData[chunkStart];
          if (refDatum) {
            rowData[field.id] = _.get(refDatum, [field.groupId, field.id]) || 0;
          } else {
            rowData[field.id] = 0;
          }
        }
      });
      rows.push(rowData);
    });

    const date = this.dateOption ? this.translate.instant('chart.dateOption.' + this.dateOption.id) : 'custom dates';
    const defaultName = (this.obj.name + ' ' + this.relatedChartData.csvTitle + ' ' + date + '.xls').replace(/ /g, '-');
    new Downloader().saveXls2(defaultName, headingFields, fields, rows, []);
  }

  private ensureStatsLoaded(): void {
    if (!this.obj?.id || !this.key || !this.resolvedChartType) {
      return;
    }

    const nextSetupKey = [
      this.key,
      this.obj.id,
      this.resolvedChartType,
      this.resolvedRelatedDataType || 'TRAFFIC'
    ].join(':');

    if (nextSetupKey !== this.setupKey) {
      this.setupChartParams();
      this.setupKey = nextSetupKey;
      this.lastRequestKey = '';
    }

    if (!this.params.startDate || !this.params.endDate) {
      return;
    }

    const requestKey = this.buildRequestKey();
    if (requestKey === this.lastRequestKey) {
      return;
    }

    this.lastRequestKey = requestKey;
    this.callForStats();
  }

  private setupChartParams(): void {
    this.pieError.set(null);
    this.relatedChartData = RELATED_CHART_DATA[this.resolvedRelatedDataType] ? RELATED_CHART_DATA[this.resolvedRelatedDataType] : RELATED_CHART_DATA.TRAFFIC;

    const chartOptions = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    const localUser = this.localUserProfile.get();
    const profile = localUser.profile;
    const profileStartDate = _.get(profile[chartOptions.perfChartOptions], 'startDate');
    const profileEndDate = _.get(profile[chartOptions.perfChartOptions], 'endDate');
    const profileCountType = ADN_CHART_COUNT_TYPES[_.get(profile[chartOptions.perfChartOptions], 'countTypeId')];
    const profileStatType = ADN_CHART_STAT_TYPES[_.get(profile[chartOptions.perfChartOptions], 'statTypeId')];

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[_.get(profile[chartOptions.perfChartOptions], 'dateOptionId')];
    if (!this.dateOption && (!profileStartDate || !profileEndDate)) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions().last12Hours;
    }
    if (_.get(this.dateOption, 'id') === 'useDates' && this.obj.startDate && this.obj.endDate) {
      this.params.startDate = moment(this.obj.startDate);
      this.params.endDate = moment(this.obj.endDate);
    } else if (this.dateOption) {
      this.params.startDate = this.dateOption.startDate();
      this.params.endDate = this.dateOption.endDate();
    } else if (profileStartDate && profileEndDate) {
      this.params.startDate = moment(profileStartDate);
      this.params.endDate = moment(profileEndDate);
    } else {
      const fallback = this.ChartOptionsUtil.getDateOptions().last12Hours;
      this.dateOption = fallback;
      this.params.startDate = fallback.startDate();
      this.params.endDate = fallback.endDate();
    }
    if (!this.params.startDate || !this.params.endDate
      || !moment(this.params.startDate).isValid() || !moment(this.params.endDate).isValid()) {
      const fallback = this.ChartOptionsUtil.getDateOptions().last12Hours;
      this.dateOption = fallback;
      this.params.startDate = fallback.startDate();
      this.params.endDate = fallback.endDate();
    }
    this.countType = profileCountType ? profileCountType : ADN_CHART_COUNT_TYPES.singular;
    this.statType = profileStatType ? profileStatType : ADN_CHART_STAT_TYPES.impression;

    this.params[this.key] = this.obj.id;
    this.allStats.set({});
    this.totals.set(null);
    this.isSpecialUser = this.localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || this.localUserProfile.get('username') === 'broker1@adnuntius.com';
    this.changeHook.chartChangeHandlers = [];
  }

  private buildRequestKey(): string {
    return [
      this.key,
      this.obj?.id,
      this.resolvedRelatedDataType || 'TRAFFIC',
      this.params.startDate?.valueOf(),
      this.params.endDate?.valueOf(),
      this.statType?.id,
      this.countType?.id
    ].join(':');
  }

  private callForStats(): void {
    this.params.groupBy = this.ChartOptionsUtil.getChunkSize(this.params.startDate, this.params.endDate, null, true).key;
    this.params.statTypeId = this.statType.id;
    this.isPending.set(true);
    const requestKey = this.lastRequestKey;

    let request: Observable<any>;
    if (this.relatedChartData.statsMethod === 'getLocation') {
      request = this.statsApi.getLocation(this.params);
    } else if (this.relatedChartData.statsMethod === 'getDomain') {
      request = this.statsApi.getDomain(this.params);
    } else {
      request = this.statsApi.getTraffic(this.params);
    }
    this.statsSubscription?.unsubscribe();
    this.statsSubscription = request.subscribe({next: (data: any) => {
      deferSignalStateWrite(() => {
        if (this.destroyed || requestKey !== this.lastRequestKey) {
          return;
        }
        this.allStats.set({
          chunks: data.chunks,
          chunkRange: data.chunkRange,
          totals: data.totals
        });
        this.totals.set(data.totals);
        this.pieError.set(null);
        this.isPending.set(false);

        this.osEmpty.set(_.isEmpty(data.totals.osType));
        this.mobileBrandEmpty.set(_.isEmpty(data.totals.mobileBrand));
        this.locationsEmpty.set(_.isEmpty(data.totals.country));
        this.domainsEmpty.set(_.isEmpty(data.totals.domain));
        this.appEmpty.set(_.isEmpty(data.totals.app));

        _.forEach(this.relatedChartData.dataTypes, (dataType: any) => {
          if (dataType.eventHook.redraw) {
            dataType.eventHook.redraw();
          }
        });

        _.forEach(this.changeHook.chartChangeHandlers, (handler) => {
          handler(this.totals());
        });
      });
    }, error: (err: any) => {
      deferSignalStateWrite(() => {
        if (this.destroyed || requestKey !== this.lastRequestKey) {
          return;
        }
        this.isPending.set(false);
        this.pieError.set(err);
        this.adnLog.warn(err);
      });
    }});
  }
}
